import {
  AloftArea,
  CompType,
  PositiveInt,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import {
  isAreaBracketed,
  isNumberBracketed,
} from '../../../../components/RecommendedCompSelection/utils/bracketing.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getCompTypeLabel, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

const BRACKETED_SUBJECT_TIP =
  "Recommend expanding your search to bracket the GLA | Site Size | Actual Age. If unavailable, comment on the Subject's marketability and if it is atypical.";

type BracketedSubjectFieldLabel = 'GLA' | 'Site Size' | 'Actual Age';

type BracketedSubjectFieldResult = {
  fieldLabel: BracketedSubjectFieldLabel;
  subjectValue: string;
  rangeValue: string;
};

const getBracketedSubjectTargetCellKey = (
  fieldLabel: BracketedSubjectFieldLabel,
  compType?: CompType,
) => {
  if (fieldLabel === 'Site Size') {
    return 'subject-editableData.site.area' as const;
  }

  const improvementsPath =
    compType === 'arv' ? 'editableData.arv.improvements' : 'editableData.improvements';
  const field = fieldLabel === 'GLA' ? 'gla' : 'age';
  return `subject-${improvementsPath}.${field}` as const;
};

const formatSqft = (value: AloftArea): string =>
  `${new Intl.NumberFormat('en-US').format(value.getUnitAmount('sqft'))} sf`;

const getBracketedAloftAreaField = ({
  subjectValue,
  compValues,
  fieldLabel,
}: {
  subjectValue: AloftArea | undefined;
  compValues: AloftArea[];
  fieldLabel: 'GLA' | 'Site Size';
}): BracketedSubjectFieldResult | undefined => {
  if (!subjectValue || compValues.length < 2) {
    return undefined;
  }

  if (isAreaBracketed(subjectValue, compValues)) {
    return undefined;
  }

  const maxCompValue = expected(AloftArea.max(compValues));
  const minCompValue = expected(AloftArea.min(compValues));

  return {
    fieldLabel,
    subjectValue: formatSqft(subjectValue),
    rangeValue: `${formatSqft(minCompValue)} - ${formatSqft(maxCompValue)}`,
  };
};

const getBracketedAgeField = ({
  subjectValue,
  compValues,
}: {
  subjectValue: PositiveInt | undefined;
  compValues: PositiveInt[];
}): BracketedSubjectFieldResult | undefined => {
  if (!isDefined(subjectValue) || compValues.length < 2) {
    return undefined;
  }

  if (isNumberBracketed(subjectValue, compValues)) {
    return undefined;
  }

  const maxCompValue = Math.max(...compValues);
  const minCompValue = Math.min(...compValues);

  return {
    fieldLabel: 'Actual Age',
    subjectValue: `${subjectValue} yrs`,
    rangeValue: `${minCompValue} - ${maxCompValue} yrs`,
  };
};

export const bracketedSubject: AlertDefinition = ({
  compSequence,
  compType,
  propertyDataEntities,
  order,
}) => {
  const subjectEntity = propertyDataEntities.subject as SubjectPropertyDataState | undefined;
  const subjectEditableData = subjectEntity?.subjectData.editableData;

  if (!subjectEditableData) {
    return [];
  }

  const scopedSubjectData = getSubjectDataForCompType(subjectEditableData, compType);

  const subjectGLA = orUndefined(schAloftArea.parse)(scopedSubjectData.improvements.gla);
  const subjectSiteSize = orUndefined(schAloftArea.parse)(scopedSubjectData.site?.area);
  const subjectAge = scopedSubjectData.improvements?.age;

  const compGLAs = compSequence
    .map((id) =>
      orUndefined(schAloftArea.parse)(
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData
          .improvements?.gla,
      ),
    )
    .filter(isDefined);

  const compSiteSizes = compSequence
    .map((id) =>
      orUndefined(schAloftArea.parse)(
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData.site
          ?.area,
      ),
    )
    .filter(isDefined);

  const compAges = compSequence
    .map(
      (id) =>
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData
          .improvements?.age,
    )
    .filter(isDefined);

  const offendingFields = [
    getBracketedAloftAreaField({
      subjectValue: subjectGLA,
      compValues: compGLAs,
      fieldLabel: 'GLA',
    }),
    getBracketedAloftAreaField({
      subjectValue: subjectSiteSize,
      compValues: compSiteSizes,
      fieldLabel: 'Site Size',
    }),
    getBracketedAgeField({
      subjectValue: subjectAge,
      compValues: compAges,
    }),
  ].filter(isDefined);

  const compTypePrefix = compType ? `“${getCompTypeLabel(compType)}” ` : '';
  const triggers = getAlertTriggers({ fixRoute: schPairedCostRouteItem.enum.features, order });

  return offendingFields.map((offendingField) => ({
    type: getScopedAlertType(schAlertType.enum.bracketedSubject, compType),
    status: 'active' as const,
    severity: 'error' as const,
    orderType: ['saas', 'appraisify'] as const,
    message: `Subject ${offendingField.fieldLabel} (${offendingField.subjectValue}) is not bracketed by ${compTypePrefix}Comps (${offendingField.rangeValue})`,
    propertyID: 'subject' as const,
    ...(compType && { compType }),
    fixingInfo: {
      tips: BRACKETED_SUBJECT_TIP,
      pairedCostRouteItem: schPairedCostRouteItem.enum.features,
      fixingMethod: ['comment', 'action'] as const,
      commentAlwaysRequired: false,
      targetCellKey: getBracketedSubjectTargetCellKey(offendingField.fieldLabel, compType),
    },
    triggers,
  }));
};
